import { Validators } from '../../../utils/validators';
import { FormModel } from '../../../components/Form';
import { getMinBookingDate, venueDependentDropdownHints } from '../../../utils/validators/HelperFunctions';
import dayjs from 'dayjs';
import { isCustomizePackage } from 'utils/pricePackage';

interface LineItemsFormParams {
  strings: any;
  venues: [];
  statuses: [];
  layouts: [];
  pricepackages: [];
  isDisabled: boolean;
  serviceChargeDropdown: boolean;
  serviceChargeList: [];
  selectedStartDate: any;
  selectedEndDate: any;
  hourlyPricing: boolean;
  isOdc: boolean;
  partOfDays?: any[];
  eventSubtypeOptions?: any[];
  guestCount?: number | string;
  excessGuestsPercentage?: number | null;
  selectedPricePackage?: any;
  pastBookingWindowDays?: number | null;
  layoutMaxCapacity?: number | null;
  layoutName?: string | null;
  venueMaxCapacity?: number | null;
  venueName?: string | null;
  layoutSelected?: boolean;
  venueSelected?: boolean;
}

/**
 * Part 1 of the booking line item drawer form: event details, schedule
 * (including the price package / total rental field which now lives
 * right after "Part of Day"), and the "Pricing Configuration" section header.
 *
 * The header at the bottom is intentionally left without trailing fields so the
 * companion {@code PricingStrategySection} React component — rendered right
 * after this form in the drawer — visually falls under "Pricing Configuration".
 *
 * The tax configuration + notes fields live in {@link lineItemsTaxForm} (Part 2)
 * so they can be rendered after the strategy/override controls without the
 * Form component having to interleave arbitrary React children mid-flow.
 */
export const lineItemsForm = ({
  strings,
  venues,
  statuses,
  layouts,
  pricepackages,
  isDisabled,
  serviceChargeDropdown,
  serviceChargeList,
  selectedStartDate,
  selectedEndDate,
  hourlyPricing,
  isOdc,
  partOfDays,
  eventSubtypeOptions,
  excessGuestsPercentage,
  selectedPricePackage,
  pastBookingWindowDays,
  layoutMaxCapacity,
  layoutName,
  venueMaxCapacity,
  venueName,
  layoutSelected = false,
  venueSelected = false,
}: LineItemsFormParams): FormModel[] => {
  const today = getMinBookingDate(pastBookingWindowDays);
  const showCustomPackageName = !hourlyPricing && isCustomizePackage(selectedPricePackage, pricepackages);
  const venueHints = venueDependentDropdownHints(venueSelected, strings);
  let arr: FormModel[] = [
    {
      label: strings.new_booking_section_event_details,
      value: '',
      type: 'section',
      placeholder: '',
      field: 'event_config_section',
      autoFocus: true,
      validators: [],
      required: false,
      responsive: { xs: 12, sm: 12 },
      compactSection: true,
    },
    {
      label: strings.new_booking_venue,
      value: {},
      size: 'medium',
      type: 'drop-down',
      placeholder: '',
      field: 'venue',
      validators: [{ check: Validators.validateMandatoryField }],
      required: true,
      responsive: { sm: 3, xs: 12 },
      options: venues,
      disabled: isDisabled,
    },
    ...(eventSubtypeOptions && eventSubtypeOptions.length > 0
      ? [
          {
            label: strings.new_booking_event_sub_type,
            value: {},
            size: 'medium',
            type: 'drop-down',
            placeholder: strings.new_booking_event_sub_type,
            field: 'eventSubtype',
            validators: [{ check: Validators.validateMandatoryField }],
            responsive: { xs: 12, sm: 3 },
            required: true,
            options: eventSubtypeOptions || [],
            disabled: false,
          } as any,
        ]
      : [
          {
            label: strings.new_booking_event_sub_type,
            value: '',
            size: 'medium',
            autoFocus: false,
            type: 'text',
            typeValue: 'text',
            variant: 'outlined',
            placeholder: '',
            field: 'eventSubtype',
            validators: [{ check: Validators.validateString }],
            responsive: { xs: 12, sm: 3 },
            required: false,
          },
        ]),
    {
      label: strings.new_booking_laouts,
      value: {},
      size: 'medium',
      type: 'drop-down',
      placeholder: strings.new_booking_laouts,
      field: 'layout',
      validators: [],
      responsive: { sm: 3, xs: 12 },
      required: false,
      isMulti: false,
      options: layouts,
      disabled: false,
      ...venueHints,
    },
    {
      label: strings.new_booking_status,
      value: {},
      size: 'medium',
      type: 'drop-down',
      placeholder: '',
      field: 'status',
      validators: [{ check: Validators.validateMandatoryField }],
      required: true,
      responsive: { sm: 3, xs: 12 },
      options: statuses,
      disabled: false,
    },
    ...(isOdc
      ? [
          {
            label: strings.customerDetailsPlaceOfEvent,
            value: '',
            size: 'medium',
            autoFocus: false,
            type: 'text',
            typeValue: 'text',
            variant: 'outlined',
            placeholder: '',
            field: 'placeOfEvent',
            validators: [{ check: Validators.validateString }],
            responsive: { xs: 12, sm: 3 },
            required: false,
          },
          {
            label: strings.customerDetailsAddress,
            value: '',
            size: 'medium',
            autoFocus: false,
            type: 'text',
            typeValue: 'text',
            variant: 'outlined',
            placeholder: '',
            field: 'addressOfEvent',
            validators: [{ check: Validators.validateString }],
            responsive: { xs: 12, sm: 6 },
            required: false,
          },
        ]
      : []),
    {
      label: strings.new_booking_number_of_guests,
      value: '',
      type: 'guest-count-split',
      placeholder: '',
      field: 'guests',
      validators: [],
      required: true,
      responsive: { sm: 6, xs: 12 },
      disabled: isDisabled,
      excessGuestsPercentage,
      venueMaxCapacity,
      venueName,
      layoutMaxCapacity,
      layoutName,
      layoutSelected,
      expectedGuestsLabel: strings.venueDetailsTabsExpected,
    },
    {
      label: strings.venue_text_excessGuestPercent,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'excessGuestsPercentage',
      validators: [{ check: Validators.validateFloat, max: 100 }],
      responsive: { sm: 3, xs: 12 },
      required: false,
      disabled: isDisabled,
      endAdornment: '%',
    },
    {
      label: strings.new_booking_service_charge,
      value: '',
      typeValue: 'number',
      type: 'text',
      placeholder: '',
      field: 'serviceChargePercentage',
      validators: [{ check: Validators.validateFloat, max: 100 }],
      required: false,
      responsive: { sm: 3, xs: 12 },
      disabled: false,
      endAdornment: '%',
    },

    {
      label: strings.new_booking_section_schedule,
      value: '',
      type: 'section',
      placeholder: '',
      field: 'schedule_config_section',
      autoFocus: true,
      validators: [],
      required: false,
      responsive: { xs: 12, sm: 12 },
      compactSection: true,
    },
    {
      label: strings.new_booking_start_date,
      value: '',
      size: 'medium',
      autoFocus: true,
      type: 'date',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'startDate',
      validators: isDisabled ? [] : [{ check: Validators.validateMandatoryField }],
      responsive: { sm: 3, xs: 12 },
      required: !isDisabled,
      minDate: dayjs(selectedStartDate).isValid() ? dayjs(selectedStartDate) : today,
      maxDate: dayjs(selectedEndDate).isValid() ? dayjs(selectedEndDate) : dayjs('2099-12-12'),
      disabled: isDisabled,
    },
    {
      label: strings.new_booking_end_date,
      value: '',
      size: 'medium',
      autoFocus: false,
      type: 'date',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'endDate',
      validators: isDisabled ? [] : [{ check: Validators.validateMandatoryField }],
      responsive: { sm: 3, xs: 12 },
      required: !isDisabled,
      minDate: dayjs(selectedStartDate).isValid() ? dayjs(selectedStartDate) : today,
      maxDate: dayjs(selectedEndDate).isValid() ? dayjs(selectedEndDate) : dayjs('2099-12-12'),
      disabled: isDisabled,
    },
    {
      label: `${strings.timeSlot_column_startTime}`,
      value: '',
      size: 'medium',
      type: 'time',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'startTime',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { sm: 3, xs: 12 },
      required: true,
      disabled: false,
    },
    {
      label: `${strings.timeSlot_text_endTime}`,
      value: '',
      size: 'medium',
      type: 'time',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'endTime',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { sm: 3, xs: 12 },
      required: true,
      disabled: false,
    },
    {
      label: strings.venueConfig_general_preLeadTime,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'preLeadTime',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateInt, max: 1440 }],
      responsive: { sm: 3, xs: 12 },
      required: true,
      disabled: false,
      endAdornment: 'min',
    },
    {
      label: strings.venueConfig_general_postLeadTime,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'postLeadTime',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateInt, max: 1440 }],
      responsive: { sm: 3, xs: 12 },
      required: true,
      disabled: false,
      endAdornment: 'min',
    },
    {
      label: `${strings.timeSlot_column_partOfDay}`,
      value: '',
      size: 'medium',
      type: 'drop-down',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'partOfDay',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { sm: 3, xs: 12 },
      required: true,
      options: partOfDays,
      disabled: false,
    },
    // Price package lives after Part of Day for non-hourly venues. Hourly total
    // rental is auto-calculated on the Pricing Configuration card instead.
    ...(hourlyPricing
      ? []
      : [
          {
            label: strings.new_booking_package,
            value: {},
            size: 'medium',
            type: 'drop-down',
            placeholder: '',
            field: 'pricePackage',
            validators: [{ check: Validators.validateMandatoryField }],
            required: true,
            responsive: { sm: 3, xs: 12 },
            options: pricepackages,
            disabled: false,
            ...venueHints,
          } as FormModel,
        ]),
    ...(showCustomPackageName
      ? [
          {
            label: strings.new_booking_custom_package_name,
            value: '',
            size: 'medium',
            autoFocus: false,
            type: 'text',
            typeValue: 'text',
            variant: 'outlined',
            placeholder: '',
            field: 'customPricePackageName',
            validators: [{ check: Validators.validateString }],
            responsive: { sm: 3, xs: 12 },
            required: false,
            disabled: false,
          } as FormModel,
        ]
      : []),

    // "Pricing Configuration" section header is intentionally the last item
    // in Part 1 — the PricingStrategySection rendered next in the drawer JSX
    // falls visually under this header.
    {
      label: 'Pricing Configuration',
      value: '',
      type: 'section',
      placeholder: '',
      field: 'financial_config_section',
      autoFocus: true,
      validators: [],
      required: false,
      responsive: { xs: 12, sm: 12 },
      compactSection: true,
    },
  ];

  if (serviceChargeDropdown) {
    const idx = arr.findIndex((i: any) => i.field === 'serviceChargePercentage');
    arr[idx] = {
      label: strings.new_booking_service_charge,
      value: {},
      size: 'medium',
      type: 'drop-down',
      placeholder: `${strings.new_booking_service_charge} `,
      field: 'serviceCharge',
      validators: [],
      responsive: { sm: 3, xs: 12 },
      required: false,
      options: serviceChargeList,
      disabled: false,
      ...venueHints,
    };
  }
  return arr;
};

/**
 * Part 2 of the booking line item drawer form: tax configuration + booking notes.
 *
 * Rendered as a separate Form below {@code PricingStrategySection} in the drawer
 * so the strategy/override controls sit cleanly between "Pricing Configuration"
 * (header from Part 1) and "Tax Configuration" (header here).
 */
export const lineItemsTaxForm = ({
  strings,
  taxGroups,
  venueSelected = false,
}: {
  strings: any;
  taxGroups: [];
  venueSelected?: boolean;
}): FormModel[] => {
  const venueHints = venueDependentDropdownHints(venueSelected, strings);
  return [
    {
      label: 'Taxes',
      value: '',
      type: 'section',
      placeholder: '',
      field: 'tax_config_section',
      autoFocus: true,
      validators: [],
      required: false,
      responsive: { xs: 12, sm: 12 },
      compactSection: true,
    },
    {
      label: strings.venueListTaxOnGuestCharge,
      value: {},
      size: 'medium',
      type: 'drop-down',
      placeholder: strings.venueListTaxOnGuestCharge,
      field: 'taxGroupOnGuestCharge',
      validators: [],
      required: false,
      responsive: { sm: 6, xs: 12 },
      options: taxGroups,
      disabled: false,
      isMulti: false,
      ...venueHints,
    },
    {
      label: strings.venueListTaxOnHallRental,
      value: {},
      size: 'medium',
      type: 'drop-down',
      placeholder: strings.venueListTaxOnHallRental,
      field: 'taxGroupOnHallRental',
      validators: [],
      required: false,
      responsive: { sm: 6, xs: 12 },
      options: taxGroups,
      disabled: false,
      isMulti: false,
      ...venueHints,
    },
    {
      label: strings.new_booking_customer_additionalComments,
      value: '',
      type: 'section',
      placeholder: '',
      field: 'event_notes_section',
      autoFocus: true,
      validators: [],
      required: false,
      responsive: { xs: 12, sm: 12 },
      compactSection: true,
    },
    {
      label: '',
      value: '',
      type: 'textEditor',
      placeholder: strings.new_booking_event_notes_placeholder,
      field: 'additionalComments',
      validators: [],
      responsive: { xs: 12 },
      required: false,
      disabled: false,
    },
  ];
};
